import React from 'react';
import PropTypes from 'prop-types';

/**
 * 기본 데이터 그리드 컴포넌트
 * 테이블 형태의 데이터를 표시
 */
export const DataGrid = ({ 
  data = [],
  columns = [],
  onRowClick,
  selectedRows = [],
  loading = false,
  emptyMessage = '데이터가 없습니다',
  className = '',
  stickyHeader = false,
  size = 'default' // 'sm', 'default', 'lg'
}) => {
  const sizeClasses = {
    sm: {
      cell: 'px-2 py-1 text-xs',
      header: 'px-2 py-2 text-xs'
    },
    default: {
      cell: 'px-3 py-2 text-sm',
      header: 'px-3 py-3 text-xs'
    },
    lg: {
      cell: 'px-4 py-3 text-base',
      header: 'px-4 py-4 text-sm'
    }
  };

  const classes = sizeClasses[size];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="size-8 animate-spin rounded-full border-b-2 border-primary"></div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="py-8 text-center text-gray-500">
        {emptyMessage}
      </div>
    );
  }

  return (
    <div className={`overflow-x-auto ${className}`}>
      <table className="min-w-full divide-y divide-gray-200">
        <thead className={`bg-gray-50 ${stickyHeader ? 'sticky top-0 z-10' : ''}`}>
          <tr>
            {columns.map((column, index) => (
              <th
                key={column.key || index}
                className={`${classes.header} text-left font-medium uppercase tracking-wider text-gray-500 ${
                  column.width ? column.width : ''
                }`}
              >
                {column.title || column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 bg-white">
          {data.map((row, rowIndex) => {
            const isSelected = selectedRows.includes(rowIndex);
            const isClickable = !!onRowClick;
            
            return (
              <tr
                key={row.id || rowIndex}
                className={`${
                  isSelected ? 'bg-blue-50' : rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                } ${
                  isClickable ? 'cursor-pointer hover:bg-gray-100' : ''
                } transition-colors`}
                onClick={() => onRowClick && onRowClick(row, rowIndex)}
              >
                {columns.map((column, colIndex) => (
                  <td
                    key={column.key || colIndex}
                    className={`${classes.cell} whitespace-nowrap text-gray-900`}
                  >
                    {column.render 
                      ? column.render(row[column.key], row, rowIndex)
                      : row[column.key]
                    }
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

/**
 * 카드 그리드 컴포넌트
 * 데이터를 카드 형태로 표시 (모바일 친화적)
 */
export const CardGrid = ({ 
  data = [],
  renderCard,
  loading = false,
  emptyMessage = '데이터가 없습니다',
  columns = 1, // 데스크톱에서의 열 수
  gap = 4,
  className = ''
}) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="size-8 animate-spin rounded-full border-b-2 border-primary"></div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="py-8 text-center text-gray-500">
        {emptyMessage}
      </div>
    );
  }

  const gridCols = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 sm:grid-cols-2',
    3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4',
    5: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5',
    6: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-6'
  };

  const gapClass = `gap-${gap}`;

  return (
    <div className={`grid ${gridCols[columns] || gridCols[3]} ${gapClass} ${className}`}>
      {data.map((item, index) => (
        <div key={item.id || index}>
          {renderCard(item, index)}
        </div>
      ))}
    </div>
  );
};

/**
 * 간단한 통계 그리드 컴포넌트
 * 4열 형태의 통계 정보 표시
 */
export const StatsGrid = ({ 
  stats = [], // [{ label, value, color, icon }]
  className = ''
}) => {
  return (
    <div className={`grid grid-cols-2 gap-3 lg:grid-cols-4 ${className}`}>
      {stats.map((stat, index) => (
        <div key={index} className="rounded-3xl bg-gray-50 p-3 text-center">
          {stat.icon && (
            <div className="mb-2 flex justify-center">
              <div className={`rounded-3xl p-2 ${
                stat.color === 'green' ? 'bg-green-100 text-green-600' :
                stat.color === 'blue' ? 'bg-blue-100 text-blue-600' :
                stat.color === 'red' ? 'bg-red-100 text-red-600' :
                'bg-gray-100 text-gray-600'
              }`}>
                {stat.icon}
              </div>
            </div>
          )}
          <div className="mb-1 text-xs text-gray-500">{stat.label}</div>
          <div className={`text-sm font-semibold ${
            stat.color === 'green' ? 'text-green-600' :
            stat.color === 'blue' ? 'text-blue-600' :
            stat.color === 'red' ? 'text-red-600' :
            'text-gray-800'
          }`}>
            {typeof stat.value === 'number' ? stat.value.toLocaleString() : stat.value}
          </div>
        </div>
      ))}
    </div>
  );
};

DataGrid.propTypes = {
  data: PropTypes.array.isRequired,
  columns: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    title: PropTypes.string,
    label: PropTypes.string,
    render: PropTypes.func,
    width: PropTypes.string
  })).isRequired,
  onRowClick: PropTypes.func,
  selectedRows: PropTypes.array,
  loading: PropTypes.bool,
  emptyMessage: PropTypes.string,
  className: PropTypes.string,
  stickyHeader: PropTypes.bool,
  size: PropTypes.oneOf(['sm', 'default', 'lg'])
};

CardGrid.propTypes = {
  data: PropTypes.array.isRequired,
  renderCard: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  emptyMessage: PropTypes.string,
  columns: PropTypes.oneOf([1, 2, 3, 4, 5, 6]),
  gap: PropTypes.number,
  className: PropTypes.string
};

StatsGrid.propTypes = {
  stats: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    color: PropTypes.string,
    icon: PropTypes.node
  })).isRequired,
  className: PropTypes.string
}; 